import { MeasurementStep, TOTAL_PROBLEMS } from './constants.js';
import { dom } from './dom.js';
import { hitTestCrossing, panView, resetViewOffset, updateViewAndDraw } from './radar.js';
import { state } from './state.js';

const PAN_THRESHOLD_PX = 4;
let panState = null;
let suppressNextClick = false;

function syncMeasurementStep() {
    if (!state.crossingMarked) {
        state.measurementStep = MeasurementStep.CROSSING;
    } else if (state.qdmConnected.aircraft1 && state.qdmConnected.aircraft2) {
        state.measurementStep = MeasurementStep.DONE;
    } else {
        state.measurementStep = MeasurementStep.AIRCRAFT;
    }
}

export function resetMeasurement() {
    state.measurementStep = MeasurementStep.CROSSING;
    state.crossingMarked = false;
    state.qdmConnected = { aircraft1: false, aircraft2: false };
    resetViewOffset();
    syncRadarCursor();
}

export function isMeasurementComplete() {
    return state.qdmConnected.aircraft1 && state.qdmConnected.aircraft2;
}

function syncRadarCursor() {
    const awaitingCrossing = state.measurementStep === MeasurementStep.CROSSING;
    dom.radarContainer.classList.toggle('radar-container--awaiting-crossing', awaitingCrossing);
}

export function syncActionButtons() {
    const submitted = dom.slider.classList.contains('submitted');
    const canSubmit = isMeasurementComplete();

    if (state.dailyCompleted) {
        dom.slider.disabled = true;
        dom.slider.classList.add('submitted');
        dom.submitBtn.classList.add('hidden');
        dom.exitDailyBtn.classList.remove('hidden');
        dom.replayLevelBtn.classList.add('hidden');
        return;
    }

    dom.exitDailyBtn.classList.add('hidden');

    if (submitted) {
        dom.submitBtn.classList.add('hidden');
        if (state.gameMode === 'time') {
            dom.replayLevelBtn.textContent = state.timeRun?.failed ? 'Try Again' : 'Next';
            dom.replayLevelBtn.classList.toggle('hidden', state.timeRun?.completed);
        } else if (state.gameMode === 'daily') {
            dom.replayLevelBtn.textContent = 'Next Level';
            dom.replayLevelBtn.classList.toggle('hidden', state.currentProblem >= TOTAL_PROBLEMS);
        } else {
            dom.replayLevelBtn.textContent = 'Play Again';
            dom.replayLevelBtn.classList.remove('hidden');
        }
    } else {
        dom.submitBtn.classList.remove('hidden');
        dom.submitBtn.disabled = !canSubmit;
        dom.replayLevelBtn.classList.add('hidden');
    }
}

export function handleCanvasClick(event) {
    if (state.measurementStep !== MeasurementStep.CROSSING) return;

    const rect = dom.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    if (!hitTestCrossing(x, y)) return;

    state.crossingMarked = true;
    syncMeasurementStep();
    syncRadarCursor();
    syncActionButtons();
    updateViewAndDraw();
}

export function handleAircraftLabelClick(aircraftKey) {
    if (!state.crossingMarked || state.qdmConnected[aircraftKey]) return;

    state.qdmConnected[aircraftKey] = true;
    syncMeasurementStep();
    syncRadarCursor();
    syncActionButtons();
    updateViewAndDraw();
}

function handlePointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    panState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastY: event.clientY,
        isPanning: false,
    };
    dom.canvas.setPointerCapture(event.pointerId);
}

function handlePointerMove(event) {
    if (!panState || panState.pointerId !== event.pointerId) return;

    const dx = event.clientX - panState.lastX;
    const dy = event.clientY - panState.lastY;
    if (!panState.isPanning) {
        const totalDx = event.clientX - panState.startX;
        const totalDy = event.clientY - panState.startY;
        if (Math.hypot(totalDx, totalDy) < PAN_THRESHOLD_PX) return;
        panState.isPanning = true;
        dom.canvas.classList.add('radar-canvas--panning');
    }

    panView(dx, dy);
    panState.lastX = event.clientX;
    panState.lastY = event.clientY;
}

function handlePointerUp(event) {
    if (!panState || panState.pointerId !== event.pointerId) return;

    const wasPanning = panState.isPanning;
    panState = null;
    dom.canvas.classList.remove('radar-canvas--panning');
    dom.canvas.releasePointerCapture(event.pointerId);

    if (wasPanning) {
        suppressNextClick = true;
        return;
    }

    handleCanvasClick(event);
}

export function initMeasurementInteraction() {
    dom.canvas.addEventListener('pointerdown', handlePointerDown);
    dom.canvas.addEventListener('pointermove', handlePointerMove);
    dom.canvas.addEventListener('pointerup', handlePointerUp);
    dom.canvas.addEventListener('pointercancel', handlePointerUp);
    dom.radarContainer.addEventListener('click', (event) => {
        if (suppressNextClick) {
            suppressNextClick = false;
            return;
        }
        const label = event.target.closest('[data-aircraft]');
        if (!label) return;
        handleAircraftLabelClick(label.dataset.aircraft);
    });
}
